import type { DataOption } from '@/entities/column'
import type { TFunction } from 'i18next'

import React, { useMemo } from 'react'
import { map, orderBy, paths, pipe, prop } from 'lodash/fp'
import { ListFilterIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { getPricingCampaignEngines } from '@/entities/pricing-campaign'
import { getUsername, useAllUsersQuery } from '@/entities/user'

import FiltersPopover from '@/shared/components/filter/FiltersPopover'
import {
	Operators,
	ValueEditorType,
	ValueType,
} from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { ampli } from '@/shared/lib/ampli'
import { toOption } from '@/shared/tools/utils'

import { usePricingCampaigns } from '../providers/PricingCampaignsProvider'

export const PricingCampaignsFilter = () => {
	const { t } = useTranslation()

	const { setRules, rules } = usePricingCampaigns()
	const usersQuery = useAllUsersQuery()
	const optimizationGroupsQuery = useOptimizationGroupsQuery<DataOption[]>({
		select: pipe([
			map(paths(['id', 'name'])),
			toOption,
			orderBy(['label'], ['asc']),
		]),
	})
	const dataChoices = useMemo(
		() => ({
			optimization_groups: optimizationGroupsQuery.data || [],
			'campaign.updated_by':
				usersQuery.data?.map((user) => ({
					label: getUsername(user),
					value: user.id,
				})) || [],
			'campaign.engine': getPricingCampaignEngines(t),
		}),
		[usersQuery.data]
	)
	const filterFields = useMemo(() => getFilterFields(t), [])

	return (
		<FiltersPopover
			enableReferencing={false}
			fields={filterFields}
			onApply={(rules) => {
				ampli.settingsPricingCampaignsFilterApply({
					rules: rules.map(prop('name')).filter(Boolean) as string[],
				})
				setRules(rules)
			}}
			dataChoices={dataChoices}
			filters={rules || []}
		>
			<Button>
				<ListFilterIcon />
				{t('general_filter')}&nbsp;
				{rules.length ? `(${rules.length})` : ''}
			</Button>
		</FiltersPopover>
	)
}

const getFilterFields = (t: TFunction) => [
	{
		value: 'campaign.name',
		label: t('pc_name'),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.text,
		enum: [],
	},
	{
		value: 'campaign.updated_by',
		label: t('last_modified_by'),
		valueType: ValueType.string,
		operations: [Operators.IN],
		valueEditorType: ValueEditorType.multiselect,
		enum: [],
	},
	{
		value: 'campaign.updated_at',
		label: t('last_modified_at'),
		valueType: ValueType.datetime,
		valueEditorType: ValueEditorType.daterange,
		enum: [],
	},
	{
		value: 'products_count',
		label: t('items_in_pc'),
		valueType: ValueType.number,
		enum: [],
	},
	{
		value: 'campaign.engine',
		label: t('general_pricing_approach'),
		valueType: ValueType.string,
		valueEditorType: ValueEditorType.select,
		enum: [],
	},
	{
		value: 'optimization_groups',
		label: t('optimization_group'),
		valueType: ValueType.array,
		valueEditorType: ValueEditorType.multiselect,
		enum: [],
	},
]
